import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

import { BillingOverview } from '@/api/billing/billing.types';
import { Bot, Clock, Euro, Phone, TrendingUp, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AssistantsRankingProps {
  overview: BillingOverview | null;
  isLoading: boolean;
  onError: (error: string) => void;
  className?: string;
}

export const AssistantsRanking = ({ overview, isLoading, onError, className }: AssistantsRankingProps) => {
  if (isLoading) {
    return (
      <Card className={cn("h-full flex flex-col", className)}>
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border">
                  <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <div className="flex items-center gap-1">
                        <Skeleton className="h-3 w-3" />
                        <Skeleton className="h-4 w-12" />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Skeleton className="h-3 w-3" />
                        <Skeleton className="h-3 w-6" />
                      </div>
                      <div className="flex items-center gap-1">
                        <Skeleton className="h-3 w-3" />
                        <Skeleton className="h-3 w-8" />
                      </div>
                      <Skeleton className="h-3 w-12" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  if (!overview?.topAssistants || overview.topAssistants.length === 0) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-base leading-none">Top Assistants</CardTitle>
              <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                Ranked by total usage cost
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[calc(100%-73px)]">
          <div className="text-center space-y-3">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Bot className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">No Assistant Data</p>
              <p className="text-xs text-muted-foreground">Usage data will appear here once assistants start making calls</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const assistants = overview.topAssistants.slice(0, 10); // Show top 10 but in scroll area

  const getRankColor = (index: number) => {
    switch (index) {
      case 0: return 'hsl(var(--primary))';
      case 1: return 'hsl(39 84% 56%)'; // amber
      case 2: return 'hsl(217 91% 60%)'; // blue
      default: return 'hsl(var(--muted-foreground))';
    }
  };

  const getRankBadgeVariant = (index: number) => {
    switch (index) {
      case 0: return 'default';
      case 1: return 'secondary';
      case 2: return 'outline';
      default: return 'outline';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
      className={className}
    >
      <Card className="h-full flex flex-col shadow-sm">
        <CardHeader className="pb-3 flex-shrink-0 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <div className="space-y-0.5">
                <CardTitle className="text-base leading-none">Top Assistants</CardTitle>
                <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                  Ranked by total usage cost
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="flex items-center gap-1 text-xs">
              <TrendingUp className="h-3 w-3" />
              By Cost
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col min-h-0 p-0">
          {/* Scrollable assistant list */}
          <ScrollArea className="flex-1 px-6 py-4">
            <div className="space-y-3 pr-2">
              {assistants.map((assistant, index) => {
                const cost = assistant.cost || 0;
                const calls = assistant.calls || 0;
                const minutes = assistant.minutes || 0;
                

                const avgCallDuration = calls > 0 ? Math.round(minutes / calls) : 0;
                const rankColor = getRankColor(index);
                
                return (
                  <motion.div
                    key={assistant.name}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02, duration: 0.2, ease: "easeOut" }}
                    className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:bg-muted/30 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-center gap-3">
                      {/* Rank indicator */}
                      <div className="flex-shrink-0">
                        <div 
                          className="flex items-center justify-center w-8 h-8 rounded-md font-semibold text-xs"
                          style={{ 
                            backgroundColor: `${rankColor}15`,
                            color: rankColor,
                            border: `1px solid ${rankColor}20`
                          }}
                        >
                          #{index + 1}
                        </div>
                      </div>
                      
                      {/* Assistant info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <h4 className="font-medium text-sm truncate">{assistant.name}</h4>
                          <div className="flex items-center gap-1 text-xs font-semibold">
                            <Euro className="h-3 w-3 text-muted-foreground" />
                            <span>{cost.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{calls}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{minutes}m</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground/70">Avg:</span>
                            <span>{avgCallDuration}m</span>
                          </div>
                        </div>

                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </motion.div>
  );
};